import type { ReactNode } from "react";

import type { BusbarSelectionRunCreateRequest } from "../services/busbarSelection";
import { downloadRunJson } from "../services/runExport";
import { BusbarSelectionForm } from "../components/BusbarSelectionForm";
import { BusbarSelectionResult } from "../components/BusbarSelectionResult";
import { StudyProjectLine } from "../components/StudyProjectLine";
import { useBusbarSelection } from "../hooks/useBusbarSelection";
import { useSldElements } from "../hooks/useSldElements";

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "The busbar selection study could not be completed.";
}

type BusbarSelectionPageProps = {
  // The switch between the two busbar modes; the host page puts it in the header.
  modePicker?: ReactNode;
};

// Study-page layout (Master Prompt v2.1 section 19): collapsible inputs beside a result-first
// column - summary, warnings, traceability, then the detail. This is EOS-07c, the module's
// second page: it chooses the busbar size and arrangement to KES-EL-BUS-002 Rev 0. The page
// owns the mutation (through the hook); the form only hands it the request.
export function BusbarSelectionPage({ modePicker }: BusbarSelectionPageProps) {
  const { calculate, reset, response, error, isPending, isError } = useBusbarSelection();
  // The active revision's single-line diagram, for the bus pickers (A27 (b)).
  const { elements: sldElements } = useSldElements();

  // Errors are surfaced through the hook state below, not thrown into the form.
  async function handleSubmit(payload: BusbarSelectionRunCreateRequest): Promise<void> {
    try {
      await calculate(payload);
    } catch {
      // Intentionally empty: isError / error already describe the failure.
    }
  }

  return (
    <main data-busbar-selection-page>
      <header>
        <p>EOS-07 · Panels and switchboards</p>
        <h1>Busbar selection</h1>
        <p>
          Choose the copper or aluminium busbar, or the compact busway, for a panel bus: the
          size and arrangement to KES-EL-BUS-002 Rev 0, held against the fault level of a stored
          short-circuit study of the same project revision. The ratings are declared evidence;
          the assembly is proven only by the builder's design verification.
        </p>
        {modePicker}
      </header>

      <div data-study-layout>
        <section aria-labelledby="busbar-selection-inputs-heading">
          <details open data-study-inputs>
            <summary>
              <h2 id="busbar-selection-inputs-heading">Inputs</h2>
            </summary>
            <StudyProjectLine />
            <BusbarSelectionForm
              disabled={isPending}
              onSubmit={handleSubmit}
              sldElements={sldElements}
            />
          </details>
        </section>

        <section aria-labelledby="busbar-selection-results-heading" data-study-results>
          <h2 id="busbar-selection-results-heading">Results</h2>

          {isPending ? (
            <p role="status" data-calculation-state="pending">
              Selecting the busbar…
            </p>
          ) : null}

          {isError ? (
            <p role="alert" data-calculation-state="error">
              {describeError(error)}
            </p>
          ) : null}

          {response ? (
            <div data-calculation-state="success">
              <BusbarSelectionResult
                response={response}
                onExport={() =>
                  downloadRunJson(
                    { run: response.run, result: response.result, data_set: response.data_set },
                    `${response.run.calculation_key}-rev${response.run.revision_number}.json`,
                  )
                }
              />
              <p>
                <button type="button" onClick={reset}>
                  Clear results
                </button>
              </p>
            </div>
          ) : null}

          {!isPending && !isError && !response ? (
            <p data-calculation-state="idle">Submit the inputs to select the busbar.</p>
          ) : null}
        </section>
      </div>

      <section aria-labelledby="busbar-selection-review-heading">
        <h2 id="busbar-selection-review-heading">Engineering review required</h2>
        <p>
          Calculation results are engineering evidence, not an automatic compliance declaration.
          Confirm the linked fault study, the design current, the fault clearing time, every
          declared rating and the builder's design verification before approval or issue.
        </p>
      </section>
    </main>
  );
}

export default BusbarSelectionPage;
